<template>
  <modal :show="show" :show-close="false" class="white-content data-export-modal" @update:show="$emit('update:show', $event)">
    <!-- Header -->
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h5 class="card-title mb-0">{{ title }}</h5>
      <span class="modal-close-icon" @click="$emit('update:show', false)">
        <i class="material-icons">close</i>
      </span>
    </div>

    <!-- Reservation options -->
    <div v-if="isReservationType" class="mb-3">
      <div class="d-flex gap-4">
        <div>
          <p class="mb-2 fw-semibold">{{ $t('dataExport.includeOptions') }}</p>
          <material-checkbox id="export-include-active" v-model="includeActive">{{ $t('dataExport.includeActiveReservations') }}</material-checkbox>
          <material-checkbox id="export-include-past" v-model="includePast">{{ $t('dataExport.includePastReservations') }}</material-checkbox>
        </div>
        <div v-if="showExternal">
          <p class="mb-2 fw-semibold">{{ $t('dataExport.includeExternalOptions') }}</p>
          <material-checkbox id="export-include-external-active" v-model="includeExternalActive">{{ $t('dataExport.includeActiveExternalReservations') }}</material-checkbox>
          <material-checkbox id="export-include-external-past" v-model="includeExternalPast">{{ $t('dataExport.includePastExternalReservations') }}</material-checkbox>
        </div>
      </div>
    </div>

    <!-- Fields -->
    <p class="mb-2 fw-semibold">{{ $t('tools.exportSelectFields') }}</p>
    <div class="d-flex flex-column gap-2">
      <material-checkbox
        id="export-field-all"
        :model-value="allSelected"
        @update:model-value="toggleAll"
      >
        {{ $t('common.all') }}
      </material-checkbox>
      <hr class="my-1" />
      <div :class="availableFields.length > 6 ? 'fields-two-col' : 'fields-single-col'">
        <material-checkbox
          v-for="field in availableFields"
          :key="field.value"
          :id="'export-field-' + field.value"
          :model-value="selectedFields.includes(field.value)"
          @update:model-value="toggleField(field.value)"
        >
          {{ field.label }}
        </material-checkbox>
      </div>
    </div>

    <!-- Footer -->
    <div class="d-flex justify-content-end mt-3">
      <button class="btn mb-0 btn-dark" :disabled="!canExport || loading" @click="doExport">
        <span v-if="loading"><i class="fas fa-spinner fa-spin me-2"></i></span>
        {{ $t('tools.exportDownload') }}
      </button>
    </div>
  </modal>
</template>

<script>
import api from '../services/api';
import config from '../config';
import Modal from './Modals/Modal.vue';
import MaterialCheckbox from './MaterialCheckbox.vue';
import * as XLSX from 'xlsx';

export default {
  name: 'DataExportModal',
  components: { Modal, MaterialCheckbox },
  props: {
    show: { type: Boolean, required: true },
    // 'tools' | 'locations' | 'projects' | 'projectReservations' | 'users' | 'userReservations'
    type: { type: String, required: true },
    // For scoped reservation exports
    projectId: { type: [String, Number], default: null },
    userId: { type: String, default: null },
    // Hide external reservation options (e.g. for regular users in UserProfile)
    showExternal: { type: Boolean, default: true },
  },
  emits: ['update:show'],
  data() {
    return {
      loading: false,
      selectedFields: [],
      includeActive: true,
      includePast: true,
      includeExternalActive: true,
      includeExternalPast: true,
    };
  },
  computed: {
    isReservationType() {
      return this.type === 'projectReservations' || this.type === 'userReservations';
    },
    title() {
      switch (this.type) {
        case 'tools': return this.$t('dataExport.exportTools');
        case 'locations': return this.$t('dataExport.exportLocations');
        case 'projects': return this.$t('dataExport.exportProjects');
        case 'projectReservations': return this.$t('dataExport.exportProjectReservations');
        case 'users': return this.$t('dataExport.exportUsers');
        case 'userReservations': return this.$t('dataExport.exportUserReservations');
        default: return this.$t('dataExport.exportData');
      }
    },
    availableFields() {
      switch (this.type) {
        case 'tools':
          return [
            { value: 'url', label: this.$t('tools.exportFieldUrl') },
            { value: 'customerToolId', label: this.$t('common.customerToolId') },
            { value: 'name', label: this.$t('common.name') },
            { value: 'description', label: this.$t('common.description') },
            { value: 'brand', label: this.$t('tools.brand') },
            { value: 'location', label: this.$t('locations.location') },
            { value: 'toolType', label: this.$t('common.type') },
            { value: 'status', label: this.$t('common.status') },
            { value: 'reservationInfo', label: this.$t('dataExport.fieldReservationInfo') },
            { value: 'sku', label: this.$t('dataExport.fieldSku') },
            { value: 'dailyPrice', label: this.$t('tools.dailyPrice') },
            { value: 'purchaseDate', label: this.$t('tools.purchaseDate') },
            { value: 'purchasePrice', label: this.$t('tools.purchasePrice') },
          ];
        case 'locations':
          return [
            { value: 'address', label: this.$t('locations.address') },
            { value: 'type', label: this.$t('common.type') },
            { value: 'latitude', label: this.$t('locations.latitude') },
            { value: 'longitude', label: this.$t('locations.longitude') },
            { value: 'radius', label: this.$t('locations.radius') },
            { value: 'requirePresence', label: this.$t('locations.requirePresenceForToolReturn') },
            { value: 'toolCount', label: this.$t('locations.toolCount') },
          ];
        case 'projects':
          return [
            { value: 'name', label: this.$t('common.name') },
            { value: 'key', label: this.$t('projects.key') },
            { value: 'description', label: this.$t('common.description') },
            { value: 'status', label: this.$t('common.status') },
            { value: 'startDate', label: this.$t('dataExport.fieldStartDate') },
            { value: 'endDate', label: this.$t('dataExport.fieldEndDate') },
            { value: 'endCustomer', label: this.$t('dataExport.fieldEndCustomer') },
          ];
        case 'projectReservations': {
          const fields = [
            { value: 'projectName', label: this.$t('common.project') },
            { value: 'projectKey', label: this.$t('projects.key') },
            { value: 'toolName', label: this.$t('common.tool') },
            { value: 'reservedBy', label: this.$t('dataExport.fieldReservedBy') },
            { value: 'quantity', label: this.$t('tools.quantity') },
            { value: 'reservedDate', label: this.$t('dataExport.fieldReservedDate') },
            { value: 'returnDate', label: this.$t('dataExport.fieldReturnDate') },
          ];
          if (this.showExternal) {
            fields.push({ value: 'externalUser', label: this.$t('tools.loanedRentedTo') });
          }
          return fields;
        }
        case 'users':
          return [
            { value: 'name', label: this.$t('common.name') },
            { value: 'email', label: this.$t('users.email') },
            { value: 'phoneNumber', label: this.$t('users.phoneNumber') },
          ];
        case 'userReservations': {
          const fields = [
            { value: 'userName', label: this.$t('common.user') },
            { value: 'projectName', label: this.$t('common.project') },
            { value: 'projectKey', label: this.$t('projects.key') },
          ];
          if (this.showExternal) {
            fields.push({ value: 'externalUser', label: this.$t('tools.loanedRentedTo') });
          }
          fields.push(
            { value: 'toolName', label: this.$t('common.tool') },
            { value: 'quantity', label: this.$t('tools.quantity') },
            { value: 'reservedDate', label: this.$t('dataExport.fieldReservedDate') },
            { value: 'returnDate', label: this.$t('dataExport.fieldReturnDate') },
          );
          return fields;
        }
        default:
          return [];
      }
    },
    allSelected() {
      return this.availableFields.length > 0 && this.selectedFields.length === this.availableFields.length;
    },
    canExport() {
      if (this.selectedFields.length === 0) return false;
      if (this.isReservationType) {
        const hasNormal = this.includeActive || this.includePast;
        const hasExternal = this.showExternal && (this.includeExternalActive || this.includeExternalPast);
        if (!hasNormal && !hasExternal) return false;
      }
      return true;
    },
  },
  watch: {
    show(val) {
      if (val) {
        this.selectedFields = this.availableFields.map(f => f.value);
        this.includeActive = true;
        this.includePast = true;
        this.includeExternalActive = true;
        this.includeExternalPast = true;
      }
    },
    type() {
      this.selectedFields = this.availableFields.map(f => f.value);
    },
    showExternal() {
      this.selectedFields = this.availableFields.map(f => f.value);
    },
  },
  methods: {
    toggleAll() {
      if (this.allSelected) {
        this.selectedFields = [];
      } else {
        this.selectedFields = this.availableFields.map(f => f.value);
      }
    },
    toggleField(value) {
      const idx = this.selectedFields.indexOf(value);
      if (idx >= 0) {
        this.selectedFields.splice(idx, 1);
      } else {
        this.selectedFields.push(value);
      }
    },
    async doExport() {
      if (!this.canExport) return;
      this.loading = true;
      try {
        switch (this.type) {
          case 'tools': await this.exportTools(); break;
          case 'locations': await this.exportLocations(); break;
          case 'projects': await this.exportProjects(); break;
          case 'projectReservations': await this.exportReservations('project'); break;
          case 'users': await this.exportUsers(); break;
          case 'userReservations': await this.exportReservations('user'); break;
        }
        this.$emit('update:show', false);
      } catch (error) {
        console.error('Export error:', error); // eslint-disable-line no-console
        this.$toast.error(this.$t('common.unableToRetrieveData'), { timeout: 0 });
      } finally {
        this.loading = false;
      }
    },

    async exportTools() {
      const [toolsResp, locationsResp, toolTypesResp] = await Promise.all([
        api.get('/Tool?currentPage=1&pageSize=9999&excludeImages=true'),
        api.get('/Location?pageSize=9999'),
        api.get('/ToolType'),
      ]);
      const items = toolsResp.data.result;
      const locationMap = Object.fromEntries((locationsResp.data.result || []).map(l => [l.id, l.address]));
      const toolTypeMap = Object.fromEntries((toolTypesResp.data || []).map(t => [t.id, t.type]));
      const baseUrl = config.baseUrl;
      const f = this.selectedFields;

      const statusMap = {
        1: this.$t('toolStatus.free'), 2: this.$t('toolStatus.inUse'),
        3: this.$t('toolStatus.broken'), 4: this.$t('toolStatus.outOfUse'),
        5: this.$t('toolStatus.loaned'), 6: this.$t('toolStatus.rented'),
        7: this.$t('toolStatus.partlyUsed'), 8: this.$t('toolStatus.fullyUsed'),
      };

      const labels = {
        url: this.$t('tools.exportFieldUrl'),
        customerToolId: this.$t('common.customerToolId'),
        name: this.$t('common.name'),
        description: this.$t('common.description'),
        brand: this.$t('tools.brand'),
        location: this.$t('locations.location'),
        toolType: this.$t('common.type'),
        status: this.$t('common.status'),
        reservationInfo: this.$t('dataExport.fieldReservationInfo'),
        sku: this.$t('dataExport.fieldSku'),
        dailyPrice: this.$t('tools.dailyPrice'),
        purchaseDate: this.$t('tools.purchaseDate'),
        purchasePrice: this.$t('tools.purchasePrice'),
      };

      const rows = items.map(item => {
        const reservation = item.toolReservations && item.toolReservations.length > 0 ? item.toolReservations[0] : null;
        const externalReservation = item.externalToolReservations && item.externalToolReservations.length > 0 ? item.externalToolReservations[0] : null;
        const activeReservation = reservation || externalReservation;
        const carGroup = item.toolToToolGroup?.toolGroup;

        const row = {};
        if (f.includes('url')) row[labels.url] = `${baseUrl}/tool/${item.id}`;
        if (f.includes('customerToolId')) row[labels.customerToolId] = item.customerToolId != null ? `#${item.customerToolId}` : '';
        if (f.includes('name')) row[labels.name] = item.name || '';
        if (f.includes('description')) row[labels.description] = item.description || '';
        if (f.includes('brand')) row[labels.brand] = item.brandName || '';
        if (f.includes('location')) row[labels.location] = locationMap[item.locationId] || '';
        if (f.includes('toolType')) row[labels.toolType] = toolTypeMap[item.toolTypeId] || '';
        if (f.includes('status')) row[labels.status] = statusMap[item.toolStatusId] || '';
        if (f.includes('reservationInfo')) {
          const parts = [];
          if (activeReservation?.project?.key) parts.push(`${this.$t('common.project')}: ${activeReservation.project.key}`);
          if (carGroup?.name) parts.push(`${this.$t('cars.car')}: ${carGroup.name}`);
          if (activeReservation?.user) parts.push(`${this.$t('dataExport.fieldReservedBy')}: ${activeReservation.user.firstName} ${activeReservation.user.lastName}`.trim());
          row[labels.reservationInfo] = parts.join(' | ');
        }
        if (f.includes('sku')) row[labels.sku] = item.sku || '';
        if (f.includes('dailyPrice')) row[labels.dailyPrice] = item.dailyPrice ?? '';
        if (f.includes('purchaseDate')) row[labels.purchaseDate] = item.purchaseDate ? new Date(item.purchaseDate).toLocaleDateString('is-IS') : '';
        if (f.includes('purchasePrice')) row[labels.purchasePrice] = item.purchasePrice ?? '';
        return row;
      });

      this.writeXlsx(rows, this.$t('tools.tools'), 'tools-export.xlsx');
    },

    async exportLocations() {
      const response = await api.get('/Location?pageSize=9999');
      const items = response.data.result;
      const f = this.selectedFields;

      const labels = {
        address: this.$t('locations.address'),
        type: this.$t('common.type'),
        toolCount: this.$t('locations.toolCount'),
        latitude: this.$t('locations.latitude'),
        longitude: this.$t('locations.longitude'),
        radius: this.$t('locations.radius'),
        requirePresence: this.$t('locations.requirePresenceForToolReturn'),
      };

      const rows = items.map(item => {
        const row = {};
        if (f.includes('address')) row[labels.address] = item.address || '';
        if (f.includes('type')) row[labels.type] = item.locationType?.type || '';
        if (f.includes('latitude')) row[labels.latitude] = item.latitude ?? '';
        if (f.includes('longitude')) row[labels.longitude] = item.longitude ?? '';
        if (f.includes('radius')) row[labels.radius] = item.radiusMeters ?? '';
        if (f.includes('requirePresence')) row[labels.requirePresence] = item.requirePresenceForToolReturn ? this.$t('common.yes') : this.$t('common.no');
        if (f.includes('toolCount')) row[labels.toolCount] = item.toolsBelongingToLocation ?? 0;
        return row;
      });

      this.writeXlsx(rows, this.$t('locations.locations'), 'locations-export.xlsx');
    },

    async exportProjects() {
      const response = await api.get('/Project?currentPage=1&pageSize=9999');
      const items = response.data.result;
      const f = this.selectedFields;

      const statusMap = {
        1: this.$t('projectStatus.notStarted'), 2: this.$t('projectStatus.active'),
        3: this.$t('projectStatus.onHold'), 4: this.$t('projectStatus.finished'),
        5: this.$t('projectStatus.other'),
      };

      const labels = {
        key: this.$t('projects.key'),
        name: this.$t('common.name'),
        description: this.$t('common.description'),
        status: this.$t('common.status'),
        startDate: this.$t('dataExport.fieldStartDate'),
        endDate: this.$t('dataExport.fieldEndDate'),
        endCustomer: this.$t('dataExport.fieldEndCustomer'),
      };

      const rows = items.map(item => {
        const row = {};
        if (f.includes('name')) row[labels.name] = item.name || '';
        if (f.includes('key')) row[labels.key] = item.key || '';
        if (f.includes('description')) row[labels.description] = item.description || '';
        if (f.includes('status')) row[labels.status] = statusMap[item.projectStatusId] || '';
        if (f.includes('startDate')) row[labels.startDate] = item.startTime ? new Date(item.startTime).toLocaleDateString('is-IS') : '';
        if (f.includes('endDate')) row[labels.endDate] = item.endTime ? new Date(item.endTime).toLocaleDateString('is-IS') : '';
        if (f.includes('endCustomer')) row[labels.endCustomer] = item.endCustomerName || '';
        return row;
      });

      this.writeXlsx(rows, this.$t('projects.projects'), 'projects-export.xlsx');
    },

    async exportReservations(mode) {
      const f = this.selectedFields;
      const allRows = [];

      const labels = {
        toolName: this.$t('common.tool'),
        projectKey: this.$t('projects.key'),
        projectName: this.$t('common.project'),
        reservedBy: this.$t('dataExport.fieldReservedBy'),
        userName: this.$t('common.user'),
        quantity: this.$t('tools.quantity'),
        externalUser: this.$t('tools.loanedRentedTo'),
        reservedDate: this.$t('dataExport.fieldReservedDate'),
        returnDate: this.$t('dataExport.fieldReturnDate'),
      };

      const scopeParam = this.projectId ? `&projectId=${this.projectId}` : (this.userId ? `&userId=${this.userId}` : '');

      const buildRow = (item, isExternal) => {
        const row = {};
        if (mode === 'project') {
          if (f.includes('projectName')) row[labels.projectName] = item.project?.name || '';
          if (f.includes('projectKey')) row[labels.projectKey] = item.project?.key || '';
          if (f.includes('toolName')) row[labels.toolName] = item.tool?.name || '';
          if (f.includes('reservedBy')) row[labels.reservedBy] = item.user ? `${item.user.firstName} ${item.user.lastName}`.trim() : '';
          if (f.includes('quantity')) row[labels.quantity] = item.quantity ?? 1;
          if (f.includes('reservedDate')) row[labels.reservedDate] = item.reservedTimestamp ? new Date(item.reservedTimestamp).toLocaleDateString('is-IS') : '';
          if (f.includes('returnDate')) row[labels.returnDate] = this.formatReturnDate(item.unreservedTimestamp);
          if (f.includes('externalUser')) row[labels.externalUser] = isExternal ? (item.externalUser || '') : '';
        } else {
          if (f.includes('userName')) row[labels.userName] = item.user ? `${item.user.firstName} ${item.user.lastName}`.trim() : '';
          if (f.includes('projectName')) row[labels.projectName] = item.project?.name || '';
          if (f.includes('projectKey')) row[labels.projectKey] = item.project?.key || '';
          if (f.includes('externalUser')) row[labels.externalUser] = isExternal ? (item.externalUser || '') : '';
          if (f.includes('toolName')) row[labels.toolName] = item.tool?.name || '';
          if (f.includes('quantity')) row[labels.quantity] = item.quantity ?? 1;
          if (f.includes('reservedDate')) row[labels.reservedDate] = item.reservedTimestamp ? new Date(item.reservedTimestamp).toLocaleDateString('is-IS') : '';
          if (f.includes('returnDate')) row[labels.returnDate] = this.formatReturnDate(item.unreservedTimestamp);
        }
        return row;
      };

      const fetchAll = async (unreserved) => {
        let page = 1;
        let fetching = true;
        while (fetching) {
          const resp = await api.get(`/Tool/Reservations?currentPage=${page}&pageSize=500&unreserved=${unreserved}&orderByReservedTimestamp=true&orderByDescending=false&excludeImages=true${scopeParam}`);
          const { result, totalItems } = resp.data;
          for (const item of result) {
            allRows.push(buildRow(item, false));
          }
          fetching = page * 500 < totalItems;
          page++;
        }
      };

      const fetchAllExternal = async (unreserved) => {
        let page = 1;
        let fetching = true;
        while (fetching) {
          const resp = await api.get(`/Tool/Reservations/External?currentPage=${page}&pageSize=500&unreserved=${unreserved}&orderByReservedTimestamp=true&orderByDescending=false&excludeImages=true${scopeParam}`);
          const { result, totalItems } = resp.data;
          for (const item of result) {
            allRows.push(buildRow(item, true));
          }
          fetching = page * 500 < totalItems;
          page++;
        }
      };

      if (this.includeActive) await fetchAll(false);
      if (this.includePast) await fetchAll(true);
      if (this.showExternal) {
        if (this.includeExternalActive) await fetchAllExternal(false);
        if (this.includeExternalPast) await fetchAllExternal(true);
      }

      const sheetName = mode === 'project' ? this.$t('dataExport.exportProjectReservations') : this.$t('dataExport.exportUserReservations');
      const fileName = mode === 'project' ? 'project-reservations-export.xlsx' : 'user-reservations-export.xlsx';
      this.writeXlsx(allRows, sheetName.substring(0, 31), fileName);
    },

    formatReturnDate(timestamp) {
      if (!timestamp) return '';
      const d = new Date(timestamp);
      if (d.getFullYear() < 1990) return '';
      return d.toLocaleDateString('is-IS');
    },

    async exportUsers() {
      const response = await api.get('/User?currentPage=1&pageSize=9999&includeDisabled=true&excludeImages=true');
      const items = response.data.result;
      const f = this.selectedFields;

      const labels = {
        name: this.$t('common.name'),
        email: this.$t('users.email'),
        phoneNumber: this.$t('users.phoneNumber'),
      };

      const rows = items.map(item => {
        const row = {};
        if (f.includes('name')) row[labels.name] = `${item.firstName} ${item.lastName}`.trim();
        if (f.includes('email')) row[labels.email] = item.email || '';
        if (f.includes('phoneNumber')) row[labels.phoneNumber] = item.phoneNumber || '';
        return row;
      });

      this.writeXlsx(rows, this.$t('users.users'), 'users-export.xlsx');
    },

    writeXlsx(rows, sheetName, fileName) {
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, sheetName);
      XLSX.writeFile(wb, fileName);
    },
  },
};
</script>

<style scoped>
.data-export-modal :deep(.modal-dialog) {
  max-width: 560px;
  max-height: 90vh;
  margin: 5vh auto;
}
.data-export-modal :deep(.modal-content) {
  max-height: 90vh;
  overflow-y: auto;
}
.modal-close-icon {
  cursor: pointer;
}
.modal-close-icon .material-icons {
  font-size: 32px;
  color: var(--primary-color);
}
.fields-two-col {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem;
}
.fields-single-col {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
</style>
